import React, { useCallback, useEffect, useMemo, useState } from "react";

// ─── StepOne.tsx ───────────────────────────────────────────────
import { useTranslation } from "react-i18next";

// React Hook Form helpers
import { useFormContext } from "react-hook-form";

// Common UI Components
import PrimaryButton from "../../common/PrimaryButton";
import TextInput from "../../hook_forms/hook_textInput";
import ImageUpload from "../../forms/imageUpload";
import Textarea from "../../hook_forms/hook_textarea";

// Styles
import styles from "./AddOrganisation.module.scss";

// ─────────────────── Zustand Store ───────────────────────────
import { useOrganisationStore } from "../../../providers/organisation/organisation.provider";

// ─── StepOne Component ─────────────────────────────────────
// This step handles uploading logo and entering basic organization details.
const StepOne = ({ onNext }: { onNext: () => void }) => {
  const { t } = useTranslation();

  // local state
  const [images, setImages] = useState<File | string>();
  const [isPrefilled, setIsPrefilled] = useState(false);

  // ─── Zustand Selectors ─────────────────────────────────────
  const selectedOrganisation = useOrganisationStore(
    (state) => state.selectedOrganisation
  );

  useEffect(() => {
    if (selectedOrganisation?.logoUrl) {
      setImages(selectedOrganisation?.logoUrl);
    }
  }, [selectedOrganisation?.logoUrl]);

  const {
    register,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useFormContext();

  const name = watch("name");
  const code = watch("code");
  const websiteUrl = watch("websiteUrl");
  const description = watch("description");

  useEffect(() => {
    if (selectedOrganisation) {
      reset({
        name: selectedOrganisation.name || "",
        code: selectedOrganisation.code || "",
        websiteUrl: selectedOrganisation.websiteUrl || "",
        description: selectedOrganisation.description || "",
        logo: selectedOrganisation.logoUrl
          ? [selectedOrganisation.logoUrl]
          : [],
      });
    } else {
      reset({
        name: "",
        code: "",
        websiteUrl: "",
        description: "",
        logo: [],
      });
    }

    setIsPrefilled(true);
  }, [reset, selectedOrganisation, setValue]);

  // handling image upload
  const handleImageUpload = useCallback(
    (e: any) => {
      const image = e?.target?.files?.[0];
      if (image) {
        setValue("logo", [image]);
        setImages(image);
      } else {
        setValue("logo", undefined); // <-- Important if no file selected
        setImages(undefined);
      }
    },
    [setValue]
  );

  const handleDeleteImage = useCallback(() => {
    setValue("logo", undefined);
    setImages(undefined);
  }, [setValue]);

  const isNextDisabled = useMemo(() => {
    return (
      !name ||
      name?.length < 2 ||
      !code ||
      code?.length < 2 ||
      !websiteUrl ||
      !!errors.websiteUrl ||
      description?.length > 500
    );
  }, [code, description?.length, errors.websiteUrl, name, websiteUrl]);

  if (!isPrefilled) return null; // or show a spinner

  return (
    <div className={styles["step_container"]}>
      <p className={styles["title"]}>{t("organisation_logo")}</p>
      {!images ? (
        <ImageUpload handleChange={handleImageUpload} />
      ) : (
        <div className={styles["uploaded_image_container"]}>
          <img
            className={styles["uploaded_image"]}
            src={
              typeof images === "string" ? images : URL.createObjectURL(images)
            }
            alt="logo"
          />
          <div onClick={handleDeleteImage} className={styles["delete_icon"]}>
            <img
              src="/assets/images/svg/delete_logo.svg"
              className={styles["icon"]}
            />
          </div>
        </div>
      )}

      {/* Name */}
      <div className={styles.inputs}>
        <TextInput
          {...register("name")}
          style={{ flex: 1 }}
          placeholder={t("organisation_name")}
          errorMessage={errors.name?.message?.toString()}
        />
      </div>

      {/* Code */}
      <div className={styles.inputs}>
        <TextInput
          {...register("code")}
          style={{ flex: 1 }}
          placeholder={t("code")}
          errorMessage={errors.code?.message?.toString()}
        />
      </div>

      {/* Website URL */}
      <div className={styles.inputs}>
        <TextInput
          {...register("websiteUrl")}
          style={{ flex: 1 }}
          placeholder={t("website_link")}
          errorMessage={errors.websiteUrl?.message?.toString()}
        />
      </div>

      {/* Description */}
      <div className={styles.inputs}>
        <Textarea
          {...register("description")}
          label={t("add_organisation_description")}
          maxCharacters={500}
          errorMessage={errors.description?.message?.toString()}
        />
      </div>

      <div className={styles["button_container"]}>
        <PrimaryButton
          color="primary"
          onClick={onNext}
          disabled={isNextDisabled}>
          {t("next")}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default StepOne;
